import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Container, Accordion, AccordionSummary, AccordionDetails, Grid } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function ThrillingFour() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  // Observe the section's visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const travelTips = [
    {
      title: "Best Time to Visit",
      content: (
        <ul>
          <li>Dry Season: May to September</li>
          <li>Monsoon Season: October to April</li>
        </ul>
      ),
    },
    {
      title: "Packing Essentials",
      content: (
        <ul>
          <li>Hiking gear</li>
          <li>Insect repellent</li>
          <li>Warm clothing</li>
          <li>Reusable water bottles</li>
          <li>Snacks for this remote region</li>
        </ul>
      ),
    },
    {
      title: "Respect Nature",
      content: "The Knuckles area is a high-security wildlife reserve. Practice 'leave no trace' principles.",
    },
    {
      title: "Mobile Reception",
      content: "Connectivity can be limited in some areas—always inform our staff when heading out.",
    },
    {
      title: "Be Prepared",
      content: "Check weather forecasts and pack accordingly.",
    },
    {
      title: "Permits & Guides",
      content: "Always use our experienced trekkers for guided hikes and secure necessary permits before exploring.",
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f9f9f9", py: 8 }} ref={sectionRef}>
      <Container>
        {/* Header */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: "bold",
              fontSize: "2.5rem",
              color: "#333",
              textTransform: "uppercase",
            }}
          >
            Travel Tips for the Knuckles Wilderness
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#555",
              fontSize: "1rem",
              mt: 2,
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Planning your journey to the heart of the Knuckles Mountain Range? Here are a few tips to ensure a seamless
            and enjoyable trip:
          </Typography>
        </Box>

        {/* Accordion Cards */}
        <Grid container spacing={4}>
          {travelTips.map((tip, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={index}
              sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(30px)",
                transition: `opacity 0.6s ease ${index * 0.2}s, transform 0.6s ease ${index * 0.2}s`,
              }}
            >
              <Accordion
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  boxShadow: 3,
                  "&:before": { display: "none" }, // Remove default accordion border
                  overflow: "hidden",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#C5A47E" }} />}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderBottom: "1px solid #ddd",
                    "& .MuiAccordionSummary-content": { margin: 0 },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "1.1rem",
                    }}
                  >
                    {tip.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    sx={{
                      color: "#555",
                      fontSize: "1rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {tip.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default ThrillingFour;
